import { skipToken, useQuery } from "@tanstack/react-query";
import {
  type CampaignImageType,
  getAdminCampaignsIdImagesImageTypePath,
  getApiUrl,
  getJwtKey,
} from "Infrastructure/Api/Api";
import { useEffect } from "react";

// Revokes the blob URL when it changes or the consumer unmounts.
// Lives in the consumer (not the query hook) so React Strict Mode's double-mount
// can't revoke the URL before the DOM has had a chance to load it.
export const useRevokeBlobUrlOnUnmount = (url: string | null | undefined) => {
  useEffect(() => {
    return () => {
      if (url?.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    };
  }, [url]);
};

const ADMIN_CAMPAIGN_IMAGE_QUERY_KEY = "ADMIN_CAMPAIGN_IMAGE";

export const adminCampaignImageKeys = {
  all: [ADMIN_CAMPAIGN_IMAGE_QUERY_KEY],
  byIdAndType: (campaignId?: number | null, imageType?: CampaignImageType) => [
    ...adminCampaignImageKeys.all,
    campaignId,
    imageType,
  ],
};

const fetchImageAsBlob = async (
  campaignId: number,
  imageType: CampaignImageType,
): Promise<string | null> => {
  const path = getAdminCampaignsIdImagesImageTypePath(campaignId, imageType);
  const url = `${getApiUrl()}${path}`;
  const token = getJwtKey();

  const response = await fetch(url, {
    headers: token ? { Authorization: token } : {},
  });

  if (response.status === 204 || response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch campaign image: ${response.status}`);
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

type Props = {
  campaignId?: number | null;
  imageType: CampaignImageType;
  fileName?: string | null;
  cacheKey?: number;
};

export const useAdminCampaignImageQuery = ({
  campaignId,
  imageType,
  fileName,
  cacheKey,
}: Props) => {
  const query = useQuery({
    queryKey: [
      ...adminCampaignImageKeys.byIdAndType(campaignId, imageType),
      cacheKey,
    ],
    queryFn:
      campaignId && fileName
        ? () => fetchImageAsBlob(campaignId, imageType)
        : skipToken,
    // Don't cache blob URLs across mounts — consumers revoke them on unmount,
    // so a cached URL would be already-revoked on remount.
    gcTime: 0,
  });

  return query;
};
